import {
  OraclePriceEntry,
  OutlierGuardConfig,
  StructuredOraclePrices,
  TokenListMap,
} from './types'
import { lenderFamily } from './lenderKeys'
import {
  DEFAULT_OUTLIER_GUARD,
  consensusReference,
  median,
} from './outlierGuard'

/**
 * Self-calibrating per-feed quality stats.
 *
 * Everything here is learned from data the price pipeline already fetches —
 * nobody ranks a lender by hand. Two observations per cycle per feed:
 *
 * 1. **Its own update cadence.** Chainlink heartbeats range from ~27s to 24h,
 *    and we cannot read a feed's configured heartbeat on-chain. Recording the
 *    gaps between distinct `updatedAt` values learns it, so "stale" becomes
 *    "far past what THIS feed normally does" instead of one global constant
 *    that is wrong for almost every feed.
 *
 * 2. **Its agreement with the rest of the market.** A feed that keeps
 *    disagreeing with every other source for the same asset is broken,
 *    whether or not it still updates. This is the only signal that catches a
 *    frozen Compound-V2-style oracle, which exposes no timestamp at all.
 *
 * The stats are a plain JSON blob: this module computes, the caller persists
 * (worker KV, a DB row, a file — margin-fetcher stays storage-agnostic).
 *
 * SAFETY: reliability may only ever DEMOTE a candidate. A cold start, a lost
 * blob or a corrupted stat therefore degrades to the structural ranking
 * rather than promoting a bad price.
 */

/** Stats for one feed, keyed `<chainId>|<feedKey>`. */
export interface FeedStat {
  /** Chain the feed lives on */
  chainId: string
  /**
   * Feed identity: the aggregator address when the oracle exposes one,
   * otherwise `lender|asset` (Compound V2 forks, Morpho `price()` — no
   * readable feed address, but still a stable identity to score).
   */
  feedKey: string
  /** Most recent `updatedAt` seen (unix seconds); 0 when the feed has none */
  lastUpdatedAt: number
  /** Wall-clock (unix seconds) of the last observation */
  lastObservedAt: number
  /** Number of distinct updates observed */
  updates: number
  /** Rolling estimate of this feed's typical update interval, seconds */
  intervalEwmaSeconds: number
  /** Largest interval observed between updates, seconds */
  intervalMaxSeconds: number
  /** Rolling |price/consensus - 1| across cycles; undefined until measurable */
  deviationEwma?: number
  /** Number of cycles where a consensus existed to compare against */
  deviationSamples: number
}

export type FeedStatsMap = { [key: string]: FeedStat }

/** Smoothing factor for both EWMAs — ~20-observation memory. */
const EWMA_ALPHA = 0.1

/**
 * Cycles of agreement data required before `classifyReliability` will act.
 * Below this, a feed is treated as unknown (neutral), never as bad.
 */
export const MIN_DEVIATION_SAMPLES = 5

/** Rolling deviation above which a feed is considered broken (25%). */
export const BAD_DEVIATION = 0.25

/** Rolling deviation above which a feed is considered degraded (5%). */
export const DEGRADED_DEVIATION = 0.05

/**
 * Multiple of a feed's own learned interval past which it counts as stale.
 * Generous because the EWMA tracks the mean while heartbeats are an upper
 * bound — a 24h-heartbeat feed that usually updates on 6h price moves must
 * not be called stale at 12h.
 */
export const STALE_INTERVAL_MULTIPLE = 4

/**
 * Minimum learned interval used for the staleness test, seconds. Stops a
 * fast feed (27s heartbeat) from being called stale after two minutes of
 * quiet, which is normal when the price does not move.
 */
export const MIN_LEARNED_STALE_SECONDS = 6 * 3600

/** Updates required before a learned interval is trusted over the default. */
export const MIN_UPDATES_FOR_INTERVAL = 4

export type FreshnessClass = 'fresh' | 'stale' | 'unknown'
export type ReliabilityClass = 'good' | 'degraded' | 'bad' | 'unknown'

/** Rank order — lower is better; used directly as a ranking tier. */
export const FRESHNESS_RANK: Record<FreshnessClass, number> = {
  fresh: 0,
  unknown: 0, // absence of evidence must not demote
  stale: 1,
}

export const RELIABILITY_RANK: Record<ReliabilityClass, number> = {
  good: 0,
  unknown: 0, // absence of evidence must not demote
  degraded: 1,
  bad: 2,
}

/**
 * Stable identity of the source behind an entry.
 *
 * Prefers the aggregator address so the many protocols reading the same
 * Chainlink feed collapse to one source — on Avalanche, Aave V3, Aave V2,
 * Granary, Nereus and Benqi all read the same AVAX/USD aggregator, and
 * counting them as five independent opinions is exactly the mistake that
 * makes consensus checks worthless.
 *
 * When no aggregator is readable (Compound V2 forks, Morpho `price()`), the
 * fallback is keyed on the protocol FAMILY, not the raw lender key: Morpho's
 * per-market keys are one protocol's opinion repeated, not N sources.
 */
export function feedKeyOf(entry: OraclePriceEntry, lender: string): string {
  if (entry.feedSource) return entry.feedSource.toLowerCase()
  return `${lenderFamily(lender)}|${entry.asset.toLowerCase()}`
}

export function feedStatKey(chainId: string, feedKey: string): string {
  return `${chainId}|${feedKey}`
}

function ewma(prev: number | undefined, sample: number): number {
  if (prev === undefined || !(prev > 0)) return sample
  return prev * (1 - EWMA_ALPHA) + sample * EWMA_ALPHA
}

/**
 * One observation of a feed within a cycle.
 * `consensusDeviation` is `|price/consensus - 1|`, omitted when the asset
 * group had no independent consensus to compare against.
 */
export interface FeedObservation {
  chainId: string
  feedKey: string
  updatedAt?: number
  consensusDeviation?: number
}

/**
 * Fold a cycle's observations into the stats blob. Pure and idempotent for
 * repeated `updatedAt` values, so re-running a cycle cannot corrupt the
 * learned cadence.
 */
export function updateFeedStats(
  prev: FeedStatsMap,
  observations: FeedObservation[],
  nowSeconds: number = Math.floor(Date.now() / 1000),
): FeedStatsMap {
  const next: FeedStatsMap = { ...prev }

  for (const obs of observations) {
    const key = feedStatKey(obs.chainId, obs.feedKey)
    const existing = next[key]

    const stat: FeedStat = existing
      ? { ...existing }
      : {
          chainId: obs.chainId,
          feedKey: obs.feedKey,
          lastUpdatedAt: 0,
          lastObservedAt: nowSeconds,
          updates: 0,
          intervalEwmaSeconds: 0,
          intervalMaxSeconds: 0,
          deviationSamples: 0,
        }

    if (obs.updatedAt && obs.updatedAt > 0) {
      // Only a CHANGED updatedAt is a new round; re-observing the same round
      // teaches nothing about cadence.
      if (obs.updatedAt > stat.lastUpdatedAt) {
        if (stat.lastUpdatedAt > 0) {
          const interval = obs.updatedAt - stat.lastUpdatedAt
          stat.intervalEwmaSeconds = ewma(stat.intervalEwmaSeconds, interval)
          stat.intervalMaxSeconds = Math.max(stat.intervalMaxSeconds, interval)
        }
        stat.lastUpdatedAt = obs.updatedAt
        stat.updates += 1
      }
    }

    if (obs.consensusDeviation !== undefined) {
      stat.deviationEwma = ewma(stat.deviationEwma, obs.consensusDeviation)
      stat.deviationSamples += 1
    }

    stat.lastObservedAt = nowSeconds
    next[key] = stat
  }

  return next
}

/**
 * Is this feed past its own normal cadence?
 *
 * Falls back to `defaultStaleSeconds` until the feed has enough observed
 * updates to trust its learned interval.
 */
export function classifyFreshness(
  updatedAt: number | undefined,
  stat: FeedStat | undefined,
  defaultStaleSeconds: number,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): FreshnessClass {
  if (!updatedAt || updatedAt <= 0) return 'unknown'
  const age = nowSeconds - updatedAt
  if (age < 0) return 'fresh'

  const learned =
    stat && stat.updates >= MIN_UPDATES_FOR_INTERVAL
      ? Math.max(
          stat.intervalEwmaSeconds * STALE_INTERVAL_MULTIPLE,
          stat.intervalMaxSeconds * 1.5,
          MIN_LEARNED_STALE_SECONDS,
        )
      : undefined

  // Take whichever bound is tighter so a learned interval can only ever make
  // the check sharper than the hand-set default, never laxer than 2x it.
  const threshold =
    learned === undefined
      ? defaultStaleSeconds
      : Math.min(learned, defaultStaleSeconds * 2)

  return age > threshold ? 'stale' : 'fresh'
}

/** How far this feed habitually sits from the rest of the market. */
export function classifyReliability(
  stat: FeedStat | undefined,
): ReliabilityClass {
  if (!stat) return 'unknown'
  if (
    stat.deviationEwma === undefined ||
    stat.deviationSamples < MIN_DEVIATION_SAMPLES
  ) {
    return 'unknown'
  }
  if (stat.deviationEwma > BAD_DEVIATION) return 'bad'
  if (stat.deviationEwma > DEGRADED_DEVIATION) return 'degraded'
  return 'good'
}

/**
 * Collect this cycle's observations straight from the structured prices.
 *
 * A feed is only scored on agreement when the rest of the group forms a
 * STRONG consensus — the same standard the selection guard applies. Learning
 * "this feed disagrees" from two other sources would repeat the mistake the
 * guard exists to avoid: for a thinly-sourced yield-bearing token, the
 * majority is often the one that is wrong, and a feed must never accumulate
 * a bad reputation for being right about something obscure.
 *
 * The reference excludes the feed being scored, so no feed is ever measured
 * against itself, and a protocol emitting one entry per market votes once.
 */
export function collectFeedObservations(
  structuredPrices: StructuredOraclePrices,
  lists: { [chainId: string]: TokenListMap } = {},
  guard: OutlierGuardConfig = DEFAULT_OUTLIER_GUARD,
): FeedObservation[] {
  interface Sample {
    chainId: string
    feedKey: string
    priceUSD: number
    updatedAt?: number
  }

  const byGroup = new Map<string, Sample[]>()
  const samples: Sample[] = []

  for (const [chainId, lenders] of Object.entries(structuredPrices)) {
    const tokenList = lists[chainId] ?? {}
    for (const [lender, entries] of Object.entries(lenders)) {
      if (!Array.isArray(entries)) continue
      for (const entry of entries) {
        if (!entry?.asset || !(entry.priceUSD > 0)) continue
        const sample: Sample = {
          chainId,
          feedKey: feedKeyOf(entry, lender),
          priceUSD: entry.priceUSD,
          updatedAt: entry.updatedAt,
        }
        samples.push(sample)
        const groupKey =
          tokenList[entry.asset]?.assetGroup ??
          `${chainId}-${entry.asset.toLowerCase()}`
        const bucket = byGroup.get(groupKey)
        if (bucket) bucket.push(sample)
        else byGroup.set(groupKey, [sample])
      }
    }
  }

  // One price per feed per group (a feed quoting a group many times is one
  // opinion), then leave-one-out median as that feed's reference.
  const observations: FeedObservation[] = []
  const seen = new Set<string>()

  for (const [, group] of byGroup) {
    const perFeed = new Map<string, number[]>()
    for (const s of group) {
      const bucket = perFeed.get(s.feedKey)
      if (bucket) bucket.push(s.priceUSD)
      else perFeed.set(s.feedKey, [s.priceUSD])
    }
    if (perFeed.size < 2) continue

    const feedPrices = [...perFeed.entries()].map(([feedKey, prices]) => ({
      feedKey,
      price: median(prices),
    }))

    for (const { feedKey, price } of feedPrices) {
      // Leave-one-out: the feed being scored never contributes to its own
      // reference, and the remainder must still form a strong consensus.
      const others = feedPrices
        .filter((f) => f.feedKey !== feedKey)
        .map((f) => ({ feedKey: f.feedKey, priceUSD: f.price }))
      const reference = consensusReference(others, guard)

      const sample = group.find((s) => s.feedKey === feedKey)!
      const dedupKey = `${sample.chainId}|${feedKey}`
      if (seen.has(dedupKey)) continue
      seen.add(dedupKey)

      observations.push({
        chainId: sample.chainId,
        feedKey,
        updatedAt: sample.updatedAt,
        // Undefined when the rest of the group does not agree on anything —
        // no verdict is recorded rather than a meaningless one.
        consensusDeviation:
          reference === undefined ? undefined : Math.abs(price / reference - 1),
      })
    }
  }

  // Feeds that had no comparable peer still contribute cadence data.
  for (const s of samples) {
    const dedupKey = `${s.chainId}|${s.feedKey}`
    if (seen.has(dedupKey)) continue
    seen.add(dedupKey)
    observations.push({
      chainId: s.chainId,
      feedKey: s.feedKey,
      updatedAt: s.updatedAt,
    })
  }

  return observations
}

/**
 * Drop stats for feeds not seen in a long time so the blob cannot grow
 * without bound as markets come and go.
 */
export function pruneFeedStats(
  stats: FeedStatsMap,
  maxAgeSeconds = 30 * 24 * 3600,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): FeedStatsMap {
  const out: FeedStatsMap = {}
  for (const [key, stat] of Object.entries(stats)) {
    if (nowSeconds - stat.lastObservedAt <= maxAgeSeconds) out[key] = stat
  }
  return out
}
